// src/pages/admin/AdminRecentRegistrations.tsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/Navbar";
import { useTranslation } from "../../hooks/useTranslation";
import { getRecentPayments } from "../../firebase/payments";
import {
  getRecentFreeRegistrations,
  getRecentPrepaidRegistrations,
  Registration,
} from "../../firebase/registrations";
import type { QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import { getCoachUsers } from "../../firebase/users";
import {
  generatePaymentPDF,
  generateDirectReceiptPDF,
} from "../../utils/generateReceiptPDF";
import { C, FONT } from "../../theme";
import { formatDate as formatDateLocalized } from "../../utils/dateHelpers";

const formatDate = (timestamp: any): string => formatDateLocalized(timestamp);

// ── Helper: extract calendar year from a Firestore Timestamp or Date ──────────
function getYear(ts: any): number {
  if (!ts) return 0;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.getFullYear();
}

interface UnifiedRow {
  id: string;
  referenceCode: string;
  coachEmail: string;
  coachName: string;
  createdAt: any;
  competitionName: string;
  competitionId: string;
  athleteCount: number;
  totalAmount: number;
  isFree: boolean;
  /** Distinguishes online / prepaid / admin_manual for badge & stats. */
  paymentType: "online" | "prepaid" | "admin_manual";
  athletes: any[];
}

const AdminRecentRegistrations: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [rows, setRows] = useState<UnifiedRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [generatingRef, setGeneratingRef] = useState<string | null>(null);

  // ── Year filter state ──────────────────────────────────────────────────────
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);

  // ── New filters: free-text search + payment-type chips ─────────────────────
  const [search, setSearch] = useState("");
  type PayChip = "all" | "online" | "prepaid" | "admin_manual";
  const [payChip, setPayChip] = useState<PayChip>("all");

  // ── Server-side pagination cursors. Each stream tracks its own
  // last-seen doc + hasMore flag. Payments stay at a flat 50 (handled
  // by getRecentPayments). Pressing "Load more" fetches the next page
  // of free + prepaid only.
  const PAGE_SIZE = 25;
  const [coachMap, setCoachMap] = useState<Map<string, { email: string; name: string }>>(new Map());
  const [freeCursor,    setFreeCursor]    = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [prepaidCursor, setPrepaidCursor] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [freeHasMore,    setFreeHasMore]    = useState(false);
  const [prepaidHasMore, setPrepaidHasMore] = useState(false);

  // Build unified UnifiedRows out of three raw streams (payments, free regs,
  // prepaid regs). The two registration streams are grouped by referenceCode
  // so each group renders as a single row (matching the old behaviour).
  const buildRows = (
    payments: Awaited<ReturnType<typeof getRecentPayments>>,
    freeRegs: Registration[],
    prepaidRegs: Registration[],
    coachLookup: Map<string, { email: string; name: string }>
  ): UnifiedRow[] => {
    const paidRows: UnifiedRow[] = payments.map((p) => {
      const coach = coachLookup.get(p.coachId);
      return {
        id: p.id ?? p.referenceCode,
        referenceCode: p.referenceCode,
        coachEmail: p.coachEmail || coach?.email || p.coachId || "—",
        coachName: coach?.name || p.coachEmail || p.coachId || "—",
        createdAt: p.createdAt,
        competitionName: p.competitionName,
        competitionId: p.competitionId,
        athleteCount: p.athleteCount,
        totalAmount: p.totalAmount,
        isFree: false,
        paymentType: "online",
        athletes: p.athletes,
      };
    });

    const groupByRef = (
      regs: Registration[],
      paymentType: UnifiedRow["paymentType"]
    ): UnifiedRow[] => {
      const groups = new Map<string, Registration[]>();
      for (const reg of regs) {
        const arr = groups.get(reg.referenceCode) ?? [];
        arr.push(reg);
        groups.set(reg.referenceCode, arr);
      }
      return Array.from(groups.entries()).map(([refCode, items]) => {
        const first = items[0];
        const coach = coachLookup.get(first.coachId);
        const email = first.coachEmail || coach?.email || first.coachId || "—";
        const name = coach?.name || email;
        const total = items.reduce((s, r) => s + (r.price ?? 0), 0);
        return {
          id: refCode,
          referenceCode: refCode,
          coachEmail: email,
          coachName: name,
          createdAt: first.registeredAt,
          competitionName: first.competitionName,
          competitionId: first.competitionId,
          athleteCount: items.length,
          totalAmount: total,
          isFree: paymentType === "admin_manual",
          paymentType,
          athletes: items.map((r) => ({
            athleteId: r.athleteId,
            firstName: r.athleteSnapshot.firstName,
            lastName: r.athleteSnapshot.lastName,
            dateOfBirth: r.athleteSnapshot.dateOfBirth,
            category: r.category,
            club: r.club,
            division: r.division,
            weightCategory: r.weightCategory,
            price: r.price ?? 0,
          })),
        };
      });
    };

    const manualRows  = groupByRef(freeRegs,    "admin_manual");
    const prepaidRows = groupByRef(prepaidRegs, "prepaid");

    return [...paidRows, ...manualRows, ...prepaidRows].sort((a, b) => {
      const aT = a.createdAt?.toDate?.()?.getTime() ?? 0;
      const bT = b.createdAt?.toDate?.()?.getTime() ?? 0;
      return bT - aT;
    });
  };

  // Initial load — first page of free + prepaid (PAGE_SIZE each) + 50 most
  // recent payments + the coach lookup. The coach map is kept on state so
  // subsequent "Load more" calls can reuse it.
  useEffect(() => {
    let cancelled = false;
    const fetchInitial = async () => {
      try {
        const [payments, freePage, prepaidPage, coachUsers] = await Promise.all([
          getRecentPayments(50),
          getRecentFreeRegistrations(PAGE_SIZE, null),
          getRecentPrepaidRegistrations(PAGE_SIZE, null),
          getCoachUsers(),
        ]);
        if (cancelled) return;

        const lookup = new Map<string, { email: string; name: string }>();
        coachUsers.forEach((u) => {
          lookup.set(u.uid, {
            email: u.email ?? "",
            name: u.displayName ?? u.email ?? u.uid,
          });
        });

        setCoachMap(lookup);
        setFreeCursor(freePage.cursor);
        setPrepaidCursor(prepaidPage.cursor);
        setFreeHasMore(freePage.hasMore);
        setPrepaidHasMore(prepaidPage.hasMore);
        setRows(buildRows(payments, freePage.rows, prepaidPage.rows, lookup));
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load registrations:", err);
        setError(t("admin.dashboard.recentRegistrations.error"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchInitial();
    return () => { cancelled = true; };
  }, [t]);

  // Append the next page of free + prepaid registrations. Payments are
  // already capped at 50 newest, so no further pagination is needed there.
  const loadMore = async () => {
    if (loadingMore || (!freeHasMore && !prepaidHasMore)) return;
    setLoadingMore(true);
    try {
      const [freePage, prepaidPage] = await Promise.all([
        freeHasMore
          ? getRecentFreeRegistrations(PAGE_SIZE, freeCursor)
          : Promise.resolve({ rows: [], cursor: freeCursor, hasMore: false }),
        prepaidHasMore
          ? getRecentPrepaidRegistrations(PAGE_SIZE, prepaidCursor)
          : Promise.resolve({ rows: [], cursor: prepaidCursor, hasMore: false }),
      ]);
      // Build only the NEW rows (no payments stream this time) and merge
      // with existing rows, keeping the date-desc sort.
      const newRows = buildRows([], freePage.rows, prepaidPage.rows, coachMap);
      setRows((prev) =>
        [...prev, ...newRows].sort((a, b) => {
          const aT = a.createdAt?.toDate?.()?.getTime() ?? 0;
          const bT = b.createdAt?.toDate?.()?.getTime() ?? 0;
          return bT - aT;
        })
      );
      setFreeCursor(freePage.cursor);
      setPrepaidCursor(prepaidPage.cursor);
      setFreeHasMore(freePage.hasMore);
      setPrepaidHasMore(prepaidPage.hasMore);
    } catch (err) {
      console.error("Load more failed:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  const hasMore = freeHasMore || prepaidHasMore;

  // ── Derive sorted list of years present in all rows ────────────────────────
  const availableYears: number[] = Array.from(
    new Set(rows.map((r) => getYear(r.createdAt)).filter((y) => y > 0))
  ).sort((a, b) => b - a); // newest first

  // Always keep current year visible even if no data yet
  if (!availableYears.includes(currentYear)) {
    availableYears.unshift(currentYear);
  }

  // ── Filter rows for the selected year + search + payment-type chip ────────
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (getYear(r.createdAt) !== selectedYear) return false;
      if (payChip !== "all" && r.paymentType !== payChip) return false;
      if (q) {
        const hay = [
          r.coachName,
          r.coachEmail,
          r.referenceCode,
          r.competitionName,
        ]
          .map((x) => (x ?? "").toLowerCase())
          .join(" ");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, selectedYear, search, payChip]);

  // Server-side pagination drives the visible list now — show all filtered
  // rows and use the "Load more" button to extend the dataset.
  const pagedRows = filteredRows;

  // ── Revenue stats — computed from filteredRows ─────────────────────────────
  const onlineRevenue = filteredRows
    .filter((r) => r.paymentType === "online")
    .reduce((s, r) => s + r.totalAmount, 0);

  const bankRevenue = filteredRows
    .filter((r) => r.paymentType === "admin_manual")
    .reduce((s, r) => s + r.totalAmount, 0);

  const prepaidRevenue = filteredRows
    .filter((r) => r.paymentType === "prepaid")
    .reduce((s, r) => s + r.totalAmount, 0);

  const totalAthletes = filteredRows.reduce((s, r) => s + r.athleteCount, 0);

  // ── PDF download ───────────────────────────────────────────────────────────
  const handleDownloadPDF = async (row: UnifiedRow) => {
    setGeneratingRef(row.referenceCode);
    try {
      if (row.paymentType === "admin_manual") {
        // Bank-transfer manual registration — full data already loaded
        await generateDirectReceiptPDF({
          referenceCode: row.referenceCode,
          competitionName: row.competitionName,
          createdAt: row.createdAt,
          totalAmount: row.totalAmount,
          paymentType: "bank",
          coachName: row.coachName,
          coachEmail: row.coachEmail,
          athletes: row.athletes,
        });
      } else if (row.paymentType === "prepaid") {
        // Prepaid (credit package) — use direct receipt, no payments collection entry
        await generateDirectReceiptPDF({
          referenceCode: row.referenceCode,
          competitionName: row.competitionName,
          createdAt: row.createdAt,
          totalAmount: row.totalAmount,
          paymentType: "bank",
          coachName: row.coachName,
          coachEmail: row.coachEmail,
          athletes: row.athletes,
        });
      } else {
        // Online paid registration — DOB fetched automatically from Firestore
        await generatePaymentPDF(
          {
            referenceCode: row.referenceCode,
            competitionName: row.competitionName,
            createdAt: row.createdAt,
            totalAmount: row.totalAmount,
            isFree: false,
            athletes: row.athletes,
          },
          { coachName: row.coachName, coachEmail: row.coachEmail }
        );
      }
    } catch (err) {
      console.error("PDF generation failed:", err);
      alert("Failed to generate PDF.");
    } finally {
      setGeneratingRef(null);
    }
  };

  // ── Badge renderer ─────────────────────────────────────────────────────────
  const renderBadge = (row: UnifiedRow) => {
    if (row.paymentType === "prepaid") {
      return (
        <span style={s.prepaidBadge}>
          {t("admin.recentRegistrations.prepaidLabel")}
        </span>
      );
    }
    if (row.paymentType === "admin_manual") {
      return (
        <span style={s.bankBadge}>
          {t("admin.recentRegistrations.adminLabel")}
        </span>
      );
    }
    return (
      <span style={s.onlineBadge}>
        {t("admin.recentRegistrations.onlineLabel")}
      </span>
    );
  };

  // ── Amount colour helper ───────────────────────────────────────────────────
  const amountColor = (row: UnifiedRow): string => {
    if (row.paymentType === "prepaid") return "#7c3aed";
    if (row.paymentType === "admin_manual") return C.blue;
    return C.red;
  };

  return (
    <div style={s.page} className="page-content">
      <Navbar />
      <div style={s.container}>

        {/* Header */}
        <div style={s.header}>
          <div style={s.pageLabel}>{t("admin.dashboard.pageLabel")}</div>
          <div style={s.heading}>{t("admin.recentRegistrations.heading")}</div>
          <div style={s.subheading}>{t("admin.recentRegistrations.subheading")}</div>
        </div>

        {/* ── Year filter bar ────────────────────────────────────────────────── */}
        {!loading && !error && availableYears.length > 0 && (
          <div style={s.yearRow}>
            <span style={s.yearRowLabel}>{t("history.year") || "Year"}</span>
            <div style={s.yearBtnGroup}>
              {availableYears.map((year) => {
                const isActive = year === selectedYear;
                return (
                  <button
                    key={year}
                    style={{
                      ...s.yearBtn,
                      ...(isActive ? s.yearBtnActive : {}),
                    }}
                    onClick={() => setSelectedYear(year)}
                  >
                    {year}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Search + payment-type chips ─────────────────────────────────── */}
        {!loading && !error && rows.length > 0 && (
          <div style={s.filterBar}>
            <input
              type="text"
              placeholder="Search coach, reference code, or competition…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={s.filterSearch}
            />
            <div style={s.chipGroup}>
              {([
                ["all",          "All"],
                ["online",       "Online"],
                ["prepaid",      "Prepaid"],
                ["admin_manual", "Bank"],
              ] as [PayChip, string][]).map(([key, label]) => (
                <button
                  key={key}
                  style={{
                    ...s.chip,
                    ...(payChip === key ? s.chipActive : {}),
                  }}
                  onClick={() => setPayChip(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Stats — only shown when filteredRows has data */}
        {!loading && !error && filteredRows.length > 0 && (
          <div style={s.statsRow}>
            <div style={s.statCard}>
              <div style={s.statNum}>{totalAthletes}</div>
              <div style={s.statLbl}>
                {t("admin.recentRegistrations.stats.totalAthletes")}
              </div>
            </div>
            <div style={s.statCard}>
              <div style={{ ...s.statNum, color: C.red }}>
                {onlineRevenue} GEL
              </div>
              <div style={s.statLbl}>
                {t("admin.recentRegistrations.stats.onlineRevenue")}
              </div>
            </div>
            <div style={s.statCard}>
              <div style={{ ...s.statNum, color: C.blue }}>
                {bankRevenue} GEL
              </div>
              <div style={s.statLbl}>
                {t("admin.recentRegistrations.stats.bankTransfer")}
              </div>
            </div>
            <div style={s.statCard}>
              <div style={{ ...s.statNum, color: "#7c3aed" }}>
                {prepaidRevenue} GEL
              </div>
              <div style={s.statLbl}>
                {t("admin.recentRegistrations.stats.prepaid")}
              </div>
            </div>
          </div>
        )}

        {loading && (
          <div style={s.stateBox}>
            {t("admin.dashboard.recentRegistrations.loading")}
          </div>
        )}
        {!loading && error && <div style={s.errorBox}>{error}</div>}

        {/* Empty — no rows at all */}
        {!loading && !error && rows.length === 0 && (
          <div style={s.emptyBox}>
            {t("admin.dashboard.recentRegistrations.empty")}
          </div>
        )}

        {/* Empty — rows exist but none in selected year */}
        {!loading && !error && rows.length > 0 && filteredRows.length === 0 && (
          <div style={s.emptyBox}>
            <div style={{ fontSize: 28, marginBottom: 12 }}>📅</div>
            <div>
              {t("history.empty.year") || `No registrations in ${selectedYear}`}
            </div>
          </div>
        )}

        {/* Table */}
        {!loading && !error && filteredRows.length > 0 && (
          <>
            <div style={s.countRow}>
              <span style={s.countBadge}>{filteredRows.length}</span>
              <span style={s.countLabel}>
                {t("admin.recentRegistrations.entriesShown")}
                {hasMore && <> · more available — click Load more below</>}
              </span>
            </div>
            <div style={s.tableWrapper}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>
                      {t("admin.dashboard.recentRegistrations.coach")}
                    </th>
                    <th style={s.th}>
                      {t("admin.dashboard.recentRegistrations.date")}
                    </th>
                    <th style={s.th}>
                      {t("admin.dashboard.recentRegistrations.competition")}
                    </th>
                    <th style={s.th}>
                      {t("admin.dashboard.recentRegistrations.athletes")}
                    </th>
                    <th style={s.th}>
                      {t("admin.dashboard.recentRegistrations.total")}
                    </th>
                    <th style={s.th}>
                      {t("admin.recentRegistrations.source")}
                    </th>
                    <th style={s.th}>
                      {t("admin.dashboard.recentRegistrations.reference")}
                    </th>
                    <th style={s.th}>
                      {t("admin.dashboard.recentRegistrations.action")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((row) => {
                    const isGenerating = generatingRef === row.referenceCode;
                    return (
                      <tr key={row.id} style={s.row}>
                        <td style={s.td}>
                          <div style={s.coachCell}>
                            <span style={s.coachName}>{row.coachName}</span>
                            {row.coachEmail !== row.coachName && (
                              <span style={s.coachEmail}>{row.coachEmail}</span>
                            )}
                          </div>
                        </td>
                        <td style={s.td}>{formatDate(row.createdAt)}</td>
                        <td
                          style={{
                            ...s.td,
                            fontWeight: 600,
                            color: C.text,
                          }}
                        >
                          {row.competitionName}
                        </td>
                        <td style={{ ...s.td, textAlign: "center" }}>
                          {row.athleteCount}
                        </td>
                        <td
                          style={{
                            ...s.td,
                            fontWeight: 700,
                            color: amountColor(row),
                          }}
                        >
                          {row.totalAmount > 0
                            ? `${row.totalAmount} GEL`
                            : row.paymentType === "admin_manual"
                            ? "—"
                            : `${row.totalAmount} GEL`}
                        </td>
                        <td style={s.td}>{renderBadge(row)}</td>
                        <td style={s.td}>
                          <span style={s.refCode}>{row.referenceCode}</span>
                        </td>
                        <td style={s.td}>
                          <div style={s.actionCell}>
                            <button
                              style={{
                                ...s.pdfBtn,
                                opacity: isGenerating ? 0.6 : 1,
                                cursor: isGenerating
                                  ? "not-allowed"
                                  : "pointer",
                              }}
                              onClick={() =>
                                !isGenerating && handleDownloadPDF(row)
                              }
                              disabled={isGenerating}
                            >
                              {isGenerating ? "⏳" : "↓ PDF"}
                            </button>
                            <button
                              style={s.detailsBtn}
                              onClick={() =>
                                navigate(
                                  `/admin/registrations/${row.competitionId}`,
                                  {
                                    state: {
                                      competitionName: row.competitionName,
                                    },
                                  }
                                )
                              }
                            >
                              {t(
                                "admin.dashboard.recentRegistrations.viewDetails"
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {hasMore && (
              <div style={s.pagerRow}>
                <button
                  style={loadingMore ? s.pagerBtnDisabled : s.pagerBtn}
                  onClick={loadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? "Loading…" : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", backgroundColor: "transparent" },
  container: { maxWidth: "1200px", margin: "0 auto", padding: "40px 24px" },
  header: { marginBottom: "24px" },
  pageLabel: {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "2px",
    color: C.red,
    marginBottom: "6px",
    textTransform: "uppercase",
  },
  heading: {
    fontFamily: FONT.display,
    fontSize: "42px",
    letterSpacing: "2px",
    color: C.text,
    lineHeight: 1,
    marginBottom: "8px",
  },
  subheading: { fontSize: "14px", color: C.text2 },

  // ── Year filter bar ──────────────────────────────────────────────────────────
  yearRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "24px",
    padding: "14px 18px",
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "10px",
  },
  yearRowLabel: {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "1.5px",
    textTransform: "uppercase",
    color: C.text3,
    flexShrink: 0,
  },
  yearBtnGroup: {
    display: "flex",
    gap: "6px",
    flexWrap: "wrap",
  },
  yearBtn: {
    fontFamily: FONT.body,
    fontSize: "13px",
    fontWeight: 700,
    letterSpacing: "0.5px",
    padding: "6px 16px",
    borderRadius: "6px",
    border: `1px solid ${C.border}`,
    backgroundColor: C.surface2,
    color: C.text2,
    cursor: "pointer",
  },
  yearBtnActive: {
    backgroundColor: C.red,
    color: "#ffffff",
    border: `1px solid ${C.red}`,
  },

  statsRow: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    marginBottom: "24px",
  },
  statCard: {
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "10px",
    padding: "14px 24px",
    textAlign: "center",
    minWidth: "120px",
  },
  statNum: {
    fontFamily: FONT.display,
    fontSize: "30px",
    color: C.text,
    lineHeight: 1,
  },
  statLbl: {
    fontSize: "10px",
    color: C.text3,
    textTransform: "uppercase",
    letterSpacing: "1px",
    marginTop: "4px",
  },
  stateBox: {
    textAlign: "center",
    padding: "60px 20px",
    color: C.text2,
    fontSize: "14px",
  },
  errorBox: {
    backgroundColor: C.redDim,
    border: `1px solid ${C.red}`,
    borderRadius: "8px",
    padding: "12px 16px",
    color: C.red,
    fontSize: "13px",
    marginBottom: "16px",
  },
  emptyBox: {
    textAlign: "center",
    padding: "60px 20px",
    backgroundColor: C.surface,
    border: `1px dashed ${C.border2}`,
    borderRadius: "12px",
    color: C.text2,
    fontSize: "14px",
  },
  countRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "12px",
  },
  countBadge: {
    backgroundColor: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: "20px",
    padding: "2px 10px",
    fontSize: "12px",
    fontWeight: 700,
    color: C.text,
  },
  countLabel: { fontSize: "12px", color: C.text3 },
  tableWrapper: {
    overflowX: "auto",
    borderRadius: "12px",
    border: `1px solid ${C.border}`,
    backgroundColor: C.surface,
  },
  table: { width: "100%", borderCollapse: "collapse", minWidth: "980px" },
  th: {
    textAlign: "left",
    padding: "12px 16px",
    backgroundColor: C.surface2,
    color: C.text2,
    fontWeight: 700,
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: `1px solid ${C.border}`,
  },
  row: { borderBottom: `1px solid ${C.border}` },
  td: {
    padding: "12px 16px",
    color: C.text2,
    fontSize: "13px",
    verticalAlign: "middle",
  },
  coachCell: { display: "flex", flexDirection: "column", gap: "2px" },
  coachName: { fontSize: "13px", fontWeight: 600, color: C.text },
  coachEmail: { fontSize: "11px", color: C.text3 },
  onlineBadge: {
    backgroundColor: C.successDim,
    color: C.success,
    borderRadius: "4px",
    padding: "2px 7px",
    fontSize: "10px",
    fontWeight: 700,
    border: `1px solid ${C.success}`,
    textTransform: "uppercase",
    letterSpacing: "0.3px",
  },
  bankBadge: {
    backgroundColor: "var(--color-info-soft)",
    color: C.blue,
    borderRadius: "4px",
    padding: "2px 7px",
    fontSize: "10px",
    fontWeight: 700,
    border: "1px solid #93c5fd",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
  },
  prepaidBadge: {
    backgroundColor: "#ede9fe",
    color: "#7c3aed",
    borderRadius: "4px",
    padding: "2px 7px",
    fontSize: "10px",
    fontWeight: 700,
    border: "1px solid #c4b5fd",
    textTransform: "uppercase",
    letterSpacing: "0.3px",
  },
  refCode: {
    fontFamily: "monospace",
    fontSize: "12px",
    backgroundColor: C.surface2,
    padding: "3px 7px",
    borderRadius: "4px",
    border: `1px solid ${C.border}`,
    color: C.text,
    fontWeight: 600,
    letterSpacing: "0.5px",
  },
  actionCell: { display: "flex", gap: "8px", flexWrap: "wrap" },
  pdfBtn: {
    backgroundColor: C.blue,
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "12px",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  detailsBtn: {
    backgroundColor: C.surface2,
    color: C.text2,
    border: `1px solid ${C.border2}`,
    borderRadius: "6px",
    padding: "5px 12px",
    fontSize: "12px",
    cursor: "pointer",
    fontWeight: 600,
    whiteSpace: "nowrap",
  },

  // ── Search + chip filter bar ─────────────────────────────────────────────
  filterBar: {
    display: "flex",
    flexWrap: "wrap",
    gap: "10px",
    alignItems: "center",
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "10px",
    padding: "10px 12px",
    marginBottom: "12px",
  },
  filterSearch: {
    flex: "1 1 240px",
    minWidth: 0,
    padding: "9px 12px",
    border: `1px solid ${C.border2}`,
    borderRadius: "7px",
    fontSize: "13px",
    backgroundColor: C.surface2,
    color: C.text,
    outline: "none",
    boxSizing: "border-box",
  },
  chipGroup: { display: "flex", flexWrap: "wrap", gap: "6px" },
  chip: {
    backgroundColor: C.surface2,
    color: C.text2,
    border: `1px solid ${C.border2}`,
    borderRadius: "20px",
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    whiteSpace: "nowrap",
    minHeight: "32px",
  },
  chipActive: {
    backgroundColor: C.red,
    color: "#fff",
    borderColor: C.red,
  },

  // ── Pagination row ───────────────────────────────────────────────────────
  pagerRow: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    marginTop: "16px",
    flexWrap: "wrap",
  },
  pagerBtn: {
    backgroundColor: C.surface,
    color: C.text,
    border: `1px solid ${C.border2}`,
    borderRadius: "6px",
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
  },
  pagerBtnDisabled: {
    backgroundColor: C.surface2,
    color: C.text3,
    border: `1px solid ${C.border}`,
    borderRadius: "6px",
    padding: "8px 14px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "not-allowed",
  },
  pagerLabel: { fontSize: "13px", color: C.text2, fontWeight: 600 },
};

export default AdminRecentRegistrations;
